import { EditOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import {
  Button,
  Input,
  Table,
  Space,
  Select,
  Form,
  message,
  Tag,
  Breadcrumb,
} from "antd";
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  getProductRequest,
  updateProductRequest,
} from "../../../api/requests/product";
import { mutationOnErrorHandler } from "../../../utils/mutationUtils";
import useDebounce from "../../../hooks/useDebounce";
import WelcomeTopbar from "../../../components/common/header/WelcomeTopbar";
import LogoLoader from "../../../components/common/LogoLoader";
import { DATE_FORMAT } from "../../../utils/date";
import { getProductStatsRequest } from "../../../api/requests/stats";
import { useProductStatsTag } from "../../../api/hooks/stats";
import { usePagination } from "../../../hooks/usePagination";

const { Search } = Input;

function ProductsPage() {
  const navigate = useNavigate();
  const [name, setName] = useState();
  const debouncedName = useDebounce(name, 500);
  const [value, setValue] = useState(1);
  const [active, setActive] = useState("all");

  const { page, pageSize, onPageChange, onShowSizeChange } = usePagination();

  const onChange = (e) => {
    console.log("radio checked", e.target.value);
    setValue(e.target.value);
  };

  const params = {
    active: active,
    name: debouncedName,
    includeActiveAssetCount: true,
    includeDecommissionedAssetCount: true,
  };

  const { data: productListRes, isLoading: isLoadingProductList } = useQuery({
    queryKey: ["product", params],
    queryFn: async () => {
      const res = await getProductRequest({
        params,
      });
      return res.data;
    },
  });

  // const { data: productStatRes, isLoading: isLoadingProductStat } =
  //   useProductStatsTag({
  //     productId: "677d176bae3f25545852ebbf",
  //     params: {},
  //   });

  const {
    mutateAsync: updateProduct,
    isPending: updatingProduct,
    variables,
  } = useMutation({
    mutationFn: async ({ id, data }) => {
      const res = await updateProductRequest({
        id,
        data,
      });
      return res.data;
    },
    mutationKey: ["product/update"],
    onSuccess: (res) => {
      const successMessage = res?.message;
      if (successMessage) {
        message.success(successMessage);
      }
    },
    onError: (error) => {
      mutationOnErrorHandler({ error, message });
    },
  });

  function navigateToUpdate(id) {
    navigate(`/setup/products/update/${id}`);
  }

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      render: (text) => text ?? "-",
    },
    {
      title: "Type",
      dataIndex: ["customAttributes", "type"],
      key: "customAttributes.type",
      render: (text) => text ?? "-",
    },
    {
      title: "Size",
      dataIndex: ["customAttributes", "size"],
      key: "customAttributes.size",
      render: (text) => text ?? "-",
    },
    {
      title: "Color",
      dataIndex: ["customAttributes", "color"],
      key: "customAttributes.color",
      render: (text) => text ?? "-",
    },
    {
      title: "Available",
      key: "activeAssetCount",
      render: ({ activeAssetCount }) => {
        return activeAssetCount ?? "-";
      },
    },
    {
      title: "Decommissioned",
      key: "decommissionedAssetCount",
      render: ({ decommissionedAssetCount }) => {
        return decommissionedAssetCount ?? "-";
      },
    },
    {
      title: "Active",
      key: "active",
      // render: (text) => (text ? "Yes" : "No") ?? "-",

      render: ({ active, _id }) => {
        return active ? (
          <Tag color="green">Active</Tag>
        ) : (
          <Tag color="red">Inactive</Tag>
        );
      },
    },
    {
      title: "Updated At",
      key: "updatedAt",
      render: ({ updatedAt }) => {
        return dayjs(updatedAt).format(DATE_FORMAT);
      },
    },
    {
      title: "Action",
      render: (details) => {
        return (
          <Space>
            <Button
              onClick={() => {
                navigateToUpdate(details._id);
              }}
              type="primary"
            >
              <EditOutlined />
            </Button>
          </Space>
        );
      },
      key: "action",
    },
  ];

  function renderTable() {
    if (isLoadingProductList) {
      return <LogoLoader />;
    }

    return (
      <Table
        dataSource={productListRes?.data || []}
        columns={columns}
        rowKey={"_id"}
        pagination={{
          total: productListRes?.count || 0,
          onChange: onPageChange,
        }}
        scroll={{
          x: true,
        }}
      />
    );
  }

  return (
    <div className="flex flex-col">
      <WelcomeTopbar />
      <div className="flex flex-col gap-6">
        <div className="flex flex-col gap-6 pb-6">
          <div className="flex min-h-14 justify-between rounded-t-xl bg-[#D1DFFF] px-6 py-3">
            <Breadcrumb
              className="bg-[#D1DFFF]"
              items={[
                {
                  title: <div className="text-lg">Products</div>,
                },
              ]}
            />
            <div>
              <Button
                onClick={() => {
                  navigate("/setup/products/add");
                }}
                type="primary"
              >
                Create Product
              </Button>
            </div>
          </div>
          <div className="flex flex-col gap-6 px-6">
            <div className="flex items-end justify-between gap-6">
              <Form.Item
                label={
                  <p className="m-0 whitespace-nowrap text-sm text-[#676666]">
                    Active
                  </p>
                }
                wrapperCol={{ sm: 24 }}
                colon={false}
                className="m-0"
              >
                <Select
                  value={active}
                  onChange={(v) => setActive(v)}
                  options={[
                    {
                      value: "all",
                      label: "All",
                    },
                    {
                      value: "true",
                      label: "Active",
                    },
                    {
                      value: "false",
                      label: "Inactive",
                    },
                  ]}
                />
              </Form.Item>
              {/* <Radio.Group onChange={onChange} value={value}>
                <Radio value={"All"}>All</Radio>
                <Radio value={"Product Type"}>Product Type</Radio>
              </Radio.Group> */}
              <div>
                <Search
                  placeholder="Search"
                  value={name}
                  onChange={(e) => {
                    setName(e.target.value);
                  }}
                />
              </div>
            </div>
            {renderTable()}
          </div>
        </div>
      </div>
    </div>
  );
}

export default ProductsPage;
